import { useState, useCallback } from 'react';
import { UnifiedMessagingService } from '@/services/api/unified-messaging.service';
import type { UnifiedMessage } from '@/types/unified-messaging';

interface UseMessageActionsProps {
  messages: UnifiedMessage[];
  setMessages: React.Dispatch<React.SetStateAction<UnifiedMessage[]>>;
  setShowReactionPicker: (messageId: string | null) => void;
  setLongPressedMessage: (messageId: string | null) => void;
  messageInputRef: React.RefObject<{ focus: () => void }>;
}

export function useMessageActions({
  messages,
  setMessages,
  setShowReactionPicker,
  setLongPressedMessage,
  messageInputRef
}: UseMessageActionsProps) {
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [reactingMessages, setReactingMessages] = useState<Set<string>>(new Set());

  const handleReaction = useCallback(async (messageId: string, emoji: string) => {
    // Add loading state
    setReactingMessages(prev => {
      const newSet = new Set(prev);
      newSet.add(messageId);
      return newSet;
    });
    
    try {
      // Check if user already reacted with this emoji
      const message = messages.find(m => m.id === messageId);
      const hasReacted = message?.user_has_reacted?.[emoji];
      
      let response;
      if (hasReacted) {
        response = await UnifiedMessagingService.getInstance().removeUnifiedReaction(messageId, emoji);
      } else {
        response = await UnifiedMessagingService.getInstance().addUnifiedReaction(messageId, emoji);
      }
      
      if (response.success) {
        // Update local state optimistically
        setMessages(prevMessages => 
          prevMessages.map(msg => {
            if (msg.id === messageId) {
              const currentReactions = { ...(msg.reaction_counts ?? {}) };
              const currentUserReactions = { ...(msg.user_has_reacted ?? {}) };
              
              if (hasReacted) {
                // Remove reaction
                currentReactions[emoji] = Math.max(0, (currentReactions[emoji] ?? 1) - 1);
                if (currentReactions[emoji] === 0) {
                  delete currentReactions[emoji];
                }
                delete currentUserReactions[emoji];
              } else {
                // Add reaction
                currentReactions[emoji] = (currentReactions[emoji] ?? 0) + 1;
                currentUserReactions[emoji] = true;
              }
              
              return {
                ...msg,
                reaction_counts: currentReactions,
                user_has_reacted: currentUserReactions
              };
            }
            return msg;
          })
        );
        
        // Clear interaction states
        setShowReactionPicker(null);
        setLongPressedMessage(null);
      } else {
        console.error('❌ Failed to update reaction:', response.error);
      }
    } catch (error) {
      console.error('❌ Error updating reaction:', error);
    } finally {
      // Remove loading state
      setReactingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  }, [messages, setMessages, setShowReactionPicker, setLongPressedMessage]);

  const startEditingMessage = useCallback((messageId: string, currentContent: string) => {
    setEditingMessage(messageId);
    setEditContent(currentContent);
    setShowReactionPicker(null);
    setLongPressedMessage(null);
  }, [setShowReactionPicker, setLongPressedMessage]);

  const cancelEditing = useCallback(() => {
    setEditingMessage(null);
    setEditContent('');
  }, []);

  const saveEditedMessage = useCallback(async () => {
    if (!editingMessage || !editContent.trim()) {
      return;
    }

    try {
      const response = await UnifiedMessagingService.getInstance().editUnifiedMessage(editingMessage, editContent);
      if (response.success && response.data) {
        setMessages(prev => prev.map(msg => 
          msg.id === editingMessage ? response.data : msg
        ));
        cancelEditing();
      }
    } catch (error) {
      console.error('✏️ Error editing message:', error);
    }
  }, [editingMessage, editContent, setMessages, cancelEditing]);

  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      const response = await UnifiedMessagingService.getInstance().deleteUnifiedMessage(messageId);
      if (response.success) {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        setShowReactionPicker(null);
        setLongPressedMessage(null);
      }
    } catch (error) {
      console.error('🗑️ Error deleting message:', error);
    }
  }, [setMessages, setShowReactionPicker, setLongPressedMessage]);

  const replyToMessage = useCallback((messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (message) {
      // Return the message to be set as replyTo in parent
      setShowReactionPicker(null);
      setLongPressedMessage(null);
      messageInputRef.current?.focus();
      return message;
    }
    return null;
  }, [messages, setShowReactionPicker, setLongPressedMessage, messageInputRef]);

  return {
    editingMessage,
    editContent,
    setEditContent,
    reactingMessages,
    handleReaction,
    startEditingMessage,
    cancelEditing,
    saveEditedMessage,
    deleteMessage,
    replyToMessage
  };
}

